import DailyFinancePanel from '../components/DailyFinancePanel';
import PendingPaymentsReport from '../components/PendingPaymentsReport';
import VendorSalesReport from '../components/VendorSalesReport';
import { externalPaymentReport } from '../utils/paymentReport';
import { isOutletRetailOrder, outletCollections } from '../utils/outletCollections';
import OutletDailyFinanceReport from '../components/OutletDailyFinanceReport';
import OutletSalesOrderDetails from '../components/OutletSalesOrderDetails';
import DispatchFinanceReport from '../components/DispatchFinanceReport';
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft,
  Banknote,
  CreditCard,
  QrCode,
  RefreshCw,
  WalletCards,
} from "lucide-react";
import { fetchPOSOrders } from "../features/orders/orderSlice";
import StockManagerLayout from "../components/StockManagerLayout";
import { API_BASE_URL } from "../utils/apiConfig";

const apiUrl = (path) => `${API_BASE_URL}${path}`;

const money = (value) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

const todayValue = () => new Date().toISOString().slice(0, 10);

const formatDateTime = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const normalizeMethod = (method) => {
  const value = String(method || "Unknown").trim();
  const lower = value.toLowerCase();

  if (lower.includes("cash")) return "Cash";
  if (lower.includes("qr") || lower.includes("upi")) return "QR / UPI";
  if (lower.includes("cc") || lower.includes("credit")) return "Credit Card";
  if (lower.includes("dc") || lower.includes("debit")) return "Debit Card";
  if (lower.includes("food") || lower.includes("fc")) return "Food Card";
  if (lower.includes("pluxee")) return "PLuxee";

  return value;
};

const normalizeCashier = (cashier) => {
  const value = String(cashier || "").trim();
  return value || "Unknown Cashier";
};

const getOrderCashier = (order) =>
  normalizeCashier(order.posUserName || order.cashierName || order.cashier);

const getOrderPaymentMethod = (order) => normalizeMethod(order.paymentMethod);

const getOrderOutlet = (order) => String(order.posLocation || "").trim() || "Unknown Outlet";

const methodIcon = {
  Cash: Banknote,
  "QR / UPI": QrCode,
  "Credit Card": CreditCard,
  "Debit Card": CreditCard,
  "Food Card": WalletCards,
  PLuxee: WalletCards,
  Unknown: WalletCards,
};

const normalizeArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.data)) return value.data;
  if (Array.isArray(value?.rows)) return value.rows;
  return [];
};

const ReportCard = ({ label, value, detail, tone = "slate" }) => {
  const toneClass =
    tone === "green"
      ? "text-green-700"
      : tone === "red"
      ? "text-red-700"
      : tone === "indigo"
      ? "text-indigo-700"
      : "text-gray-900";

  return (
    <div className="rounded-lg border bg-white p-4 shadow-sm">
      <div className="text-sm font-semibold text-gray-500">{label}</div>
      <div className={`mt-2 text-2xl font-bold ${toneClass}`}>{value}</div>
      {detail ? <div className="mt-1 text-xs text-gray-500">{detail}</div> : null}
    </div>
  );
};

export default function AccountsPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isInventoryView =
    new URLSearchParams(location.search).get("from") === "inventory";

  const posUserInfo = useSelector((s) => s.posUser.userInfo);
  const sourceOrders = useSelector((s) => s.orders?.posOrdersList || []);
  const collectionOrders = useMemo(() => outletCollections(sourceOrders), [sourceOrders]);
  const loading = useSelector((s) => s.orders?.posOrdersListLoading || false);
  const error = useSelector((s) => s.orders?.posOrdersListError || "");

  const [fromDate, setFromDate] = useState(todayValue());
  const [toDate, setToDate] = useState(todayValue());
  const [localError, setLocalError] = useState("");
  const [financeReportError, setFinanceReportError] = useState("");
  const [financeReportLoading, setFinanceReportLoading] = useState(false);
  const [financeReports, setFinanceReports] = useState({
    products: [],
    purchases: [],
    outlets: [],
    packLedgers: [],
    ledgers: [],
  });
  const [dailyFinanceRefresh, setDailyFinanceRefresh] = useState(0);
  const [activeFinanceReportCard, setActiveFinanceReportCard] = useState("reports");

  const [selectedCashier, setSelectedCashier] = useState("all");
  const [selectedPaymentMode, setSelectedPaymentMode] = useState("all");
  const [selectedOutlet, setSelectedOutlet] = useState("");
  const [detailsOrderId, setDetailsOrderId] = useState(null);
  const outletOptions = useMemo(() => {
    const outlets = new Set(sourceOrders.filter(isOutletRetailOrder).map(getOrderOutlet));
    // Keep the selection visible when a new date range has no sales for it.
    if (selectedOutlet) outlets.add(selectedOutlet);
    return [...outlets].sort((a, b) => a.localeCompare(b));
  }, [sourceOrders, selectedOutlet]);
  const orders = useMemo(
    () => selectedOutlet
      ? collectionOrders.filter(order => getOrderOutlet(order) === selectedOutlet)
      : collectionOrders,
    [collectionOrders, selectedOutlet]
  );
  const billedOrders = useMemo(() => sourceOrders.filter(isOutletRetailOrder).filter(order => !selectedOutlet || getOrderOutlet(order) === selectedOutlet), [sourceOrders, selectedOutlet]);
  const billedTotal = billedOrders.reduce((sum, order) => sum + Number(order.totalPrice || 0), 0);
  const pendingTotal = billedOrders.reduce((sum, order) => sum + Math.max(0, Number(order.totalPrice || 0) - (order.isPaid ? Number(order.totalPrice || 0) : Number(order.paidAmount || 0))), 0);
  const token = posUserInfo?.token;
  const [pendingPayments, setPendingPayments] = useState(null);
  const [vendorTodayTotals, setVendorTodayTotals] = useState({ sales: 0, payments: 0, loading: true, error: '' });
  const [pendingLoading, setPendingLoading] = useState(false);
  const [pendingError, setPendingError] = useState('');
  const loadPendingPayments = useCallback(async () => {
    if (!token) return;
    setPendingLoading(true); setPendingError('');
    try {
      const response = await axios.get(apiUrl('/orders/pos/pending-payments?includePaidDispatches=true'), { headers: { Authorization: `Bearer ${token}` } });
      setPendingPayments(externalPaymentReport(response.data));
    } catch (err) { setPendingError(err.response?.data?.message || 'Unable to load pending payments.'); }
    finally { setPendingLoading(false); }
  }, [token]);
  useEffect(() => { loadPendingPayments(); }, [loadPendingPayments]);

  const authConfig = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  const loadAccounts = useCallback(async () => {
    if (!token) return;

    setLocalError("");

    try {
      if (fromDate === todayValue() && toDate === todayValue()) {
        await dispatch(fetchPOSOrders({ mode: "today" })).unwrap();
        return;
      }

      await dispatch(
        fetchPOSOrders({ mode: "custom", from: fromDate, to: toDate })
      ).unwrap();
    } catch (err) {
      setLocalError(
        err ||
          "Unable to load finance data. Please try Refresh."
      );
    }
  }, [dispatch, fromDate, toDate, token]);

  const loadFinanceReports = useCallback(async () => {
    if (!token) return;

    setFinanceReportLoading(true);
    setFinanceReportError("");

    try {
      await axios.post(apiUrl("/accounts/ensure"), {}, authConfig);
      const [outletRes] =
        await Promise.all([


          axios.get(apiUrl("/accounts/summary?dimension=outlet"), authConfig),


        ]);

      setFinanceReports({
        products: [],
        purchases: [],
        outlets: normalizeArray(outletRes.data),
        packLedgers: [],
        ledgers: [],
      });
    } catch (err) {
      setFinanceReportError(
        err.response?.data?.message || err.message || "Unable to load product finance reports"
      );
    } finally {
      setFinanceReportLoading(false);
    }
  }, [authConfig, token]);

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  useEffect(() => {
    loadFinanceReports();
  }, [loadFinanceReports]);

  const ordersForCashierDropdown = useMemo(
    () =>
      selectedPaymentMode === "all"
        ? orders
        : orders.filter(
            (order) => getOrderPaymentMethod(order) === selectedPaymentMode
          ),
    [orders, selectedPaymentMode]
  );

  const ordersForPaymentDropdown = useMemo(
    () =>
      selectedCashier === "all"
        ? orders
        : orders.filter((order) => getOrderCashier(order) === selectedCashier),
    [orders, selectedCashier]
  );

  const selectedOrders = useMemo(
    () =>
      orders.filter((order) => {
        const cashierMatches =
          selectedCashier === "all" || getOrderCashier(order) === selectedCashier;
        const paymentMatches =
          selectedPaymentMode === "all" ||
          getOrderPaymentMethod(order) === selectedPaymentMode;

        return cashierMatches && paymentMatches;
      }),
    [orders, selectedCashier, selectedPaymentMode]
  );

  const allPaymentSummary = useMemo(() => {
    const grouped = {};

    for (const order of orders) {
      const method = getOrderPaymentMethod(order);
      if (!grouped[method]) {
        grouped[method] = {
          method,
          count: 0,
          amount: 0,
        };
      }

      grouped[method].count += 1;
      grouped[method].amount += Number(order.totalPrice || 0);
    }

    return Object.values(grouped).sort((a, b) => b.amount - a.amount);
  }, [orders]);

  const paymentSummary = useMemo(() => {
    const grouped = {};

    for (const order of ordersForPaymentDropdown) {
      const method = getOrderPaymentMethod(order);
      if (!grouped[method]) {
        grouped[method] = {
          method,
          count: 0,
          amount: 0,
        };
      }

      grouped[method].count += 1;
      grouped[method].amount += Number(order.totalPrice || 0);
    }

    return Object.values(grouped).sort((a, b) => b.amount - a.amount);
  }, [ordersForPaymentDropdown]);

  const cashierSummary = useMemo(() => {
    const grouped = {};

    for (const order of ordersForCashierDropdown) {
      const cashier = getOrderCashier(order);

      if (!grouped[cashier]) {
        grouped[cashier] = {
          cashier,
          count: 0,
          amount: 0,
        };
      }

      grouped[cashier].count += 1;
      grouped[cashier].amount += Number(order.totalPrice || 0);
    }

    return Object.values(grouped).sort((a, b) => b.amount - a.amount);
  }, [ordersForCashierDropdown]);

  const totals = useMemo(
    () => ({
      orders: orders.length,
      amount: orders.reduce(
        (sum, order) => sum + Number(order.totalPrice || 0),
        0
      ),
      cash: orders
        .filter((order) => getOrderPaymentMethod(order) === "Cash")
        .reduce((sum, order) => sum + Number(order.totalPrice || 0), 0),
      digital: orders
        .filter((order) => getOrderPaymentMethod(order) !== "Cash")
        .reduce((sum, order) => sum + Number(order.totalPrice || 0), 0),
    }),
    [orders]
  );

  const selectedTotal = useMemo(
    () => ({
      count: selectedOrders.length,
      amount: selectedOrders.reduce(
        (sum, order) => sum + Number(order.totalPrice || 0),
        0
      ),
    }),
    [selectedOrders]
  );

  const selectedCashierTotal = useMemo(() => {
    const cashierOrders =
      selectedCashier === "all"
        ? orders
        : orders.filter((order) => getOrderCashier(order) === selectedCashier);

    return {
      label: selectedCashier === "all" ? "All Cashiers" : selectedCashier,
      count: cashierOrders.length,
      amount: cashierOrders.reduce(
        (sum, order) => sum + Number(order.totalPrice || 0),
        0
      ),
    };
  }, [orders, selectedCashier]);

  const selectedPaymentTotal = useMemo(() => {
    const paymentOrders =
      selectedCashier === "all" && selectedPaymentMode === "all"
        ? orders
        : orders.filter((order) => {
            const cashierMatches =
              selectedCashier === "all" ||
              getOrderCashier(order) === selectedCashier;
            const paymentMatches =
              selectedPaymentMode === "all" ||
              getOrderPaymentMethod(order) === selectedPaymentMode;

            return cashierMatches && paymentMatches;
          });

    return {
      label:
        selectedPaymentMode === "all" && selectedCashier !== "all"
          ? `All Payment Modes by ${selectedCashier}`
          : selectedPaymentMode === "all"
          ? "All Payment Modes"
          : selectedCashier === "all"
          ? selectedPaymentMode
          : `${selectedPaymentMode} by ${selectedCashier}`,
      count: paymentOrders.length,
      amount: paymentOrders.reduce(
        (sum, order) => sum + Number(order.totalPrice || 0),
        0
      ),
    };
  }, [orders, selectedCashier, selectedPaymentMode]);

  const financeReportTotals = useMemo(() => {
    const productRows = financeReports.products;
    const purchaseRows = financeReports.purchases;
    const outletRows = financeReports.outlets;

    return {
      productCount: productRows.length,
      purchaseCount: purchaseRows.length,
      outletCount: outletRows.length,
      purchaseCost: purchaseRows.reduce((sum, row) => sum + Number(row.purchase_cost || 0), 0),
      finalCost: productRows.reduce((sum, row) => sum + Number(row.final_cost || 0), 0),
      targetSale: productRows.reduce((sum, row) => sum + Number(row.target_sale_value || 0), 0),
      // The settlement card represents all POS collections in the selected period,
      // including sales that have not been linked to inventory batch costs.
      outletSales: totals.amount,
      profit: productRows.reduce((sum, row) => sum + Number(row.profit_loss || 0), 0),
    };
  }, [financeReports, totals.amount]);



  useEffect(() => {
    if (
      selectedCashier !== "all" &&
      !cashierSummary.some((item) => item.cashier === selectedCashier)
    ) {
      setSelectedCashier("all");
    }
  }, [cashierSummary, selectedCashier]);

  useEffect(() => {
    if (
      selectedPaymentMode !== "all" &&
      !paymentSummary.some((item) => item.method === selectedPaymentMode)
    ) {
      setSelectedPaymentMode("all");
    }
  }, [paymentSummary, selectedPaymentMode]);

  const reloadApp = () => {
    window.location.reload();
  };

  const content = (
    <div
      className={
        isInventoryView
          ? "min-h-full pb-8"
          : "fixed inset-0 overflow-y-scroll bg-slate-100 p-3 pb-24 md:p-5 md:pb-10 [-webkit-overflow-scrolling:touch]"
      }
    >
      <div className={isInventoryView ? "pb-8" : "mx-auto max-w-7xl pb-8"}>
        <div className="flex flex-col gap-3 rounded-lg border bg-white p-4 shadow-sm md:flex-row md:items-center md:justify-between">
          <div>
            {!isInventoryView ? (
              <button
                onClick={() => navigate("/pos")}
                className="mb-3 inline-flex h-10 min-w-[110px] items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-4 text-sm font-semibold text-gray-700 hover:bg-gray-50"
              >
                <ArrowLeft size={16} />
                POS
              </button>
            ) : null}
            <h1 className="text-2xl font-bold text-gray-900">
              Finance Settlement
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              Check payment totals by cash, QR scan, cards, and other modes.
            </p>
          </div>

          {!isInventoryView ? (
            <div className="rounded-lg bg-slate-50 px-4 py-3 text-sm">
              <div className="font-bold text-gray-800">
                {posUserInfo?.username || "Cashier"}
              </div>
              <div className="text-gray-500">{posUserInfo?.location || "-"}</div>
            </div>
          ) : null}
        </div>

        <section className="mt-4 overflow-hidden rounded-lg border bg-white shadow-sm">
          <div className="flex flex-col gap-3 border-b bg-white p-4 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-lg font-bold text-gray-900">
                {activeFinanceReportCard === "outletSales" ? "Outlet Sales" : "Finance Reports"}
              </h2>
              <p className="text-sm text-gray-500">
                {activeFinanceReportCard === "outletSales"
                  ? "Review outlet collection, cashier settlement, payment modes, and sales."
                  : "Ledger, product, and purchase view from product financials."}
              </p>
            </div>
            <button
              type="button"
              onClick={activeFinanceReportCard === "dailyFinance" ? () => setDailyFinanceRefresh(value => value + 1) : ['pendingPayments', 'vendorSales'].includes(activeFinanceReportCard) ? loadPendingPayments : loadFinanceReports}
              disabled={financeReportLoading}
              className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-4 text-sm font-bold text-gray-700 hover:bg-gray-50 disabled:opacity-60"
            >
              <RefreshCw size={16} />
              {financeReportLoading
                ? "Loading"
                : activeFinanceReportCard === "outletSales"
                ? "Refresh Sales"
                : "Refresh Reports"}
            </button>
          </div>

          <div className="bg-slate-50 p-4">
            {financeReportError ? (
              <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">
                {financeReportError}
              </div>
            ) : null}

            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
              <button
                type="button"
                onClick={() => {
                  setActiveFinanceReportCard("reports");
                }}
                className={`rounded-lg border p-4 text-left shadow-sm transition ${
                  activeFinanceReportCard === "reports"
                    ? "border-indigo-300 bg-indigo-50 ring-2 ring-indigo-100"
                    : "bg-white hover:border-slate-300 hover:bg-slate-50"
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold text-gray-500">Reports</div>
                    <div className="mt-2 text-2xl font-bold text-indigo-700">
                      Batch-wise
                    </div>
                  </div>
                  <div className="rounded-full bg-white px-3 py-1 text-xs font-bold text-indigo-700">
                    Dispatch Finance
                  </div>
                </div>
                <div className="mt-2 text-xs text-gray-500">
                  Batch stock, dispatch destinations, amounts and margins.
                </div>
              </button>

              <button
                type="button"
                onClick={() => {
                  setActiveFinanceReportCard("outletSales");
                }}
                className={`rounded-lg border p-4 text-left shadow-sm transition ${
                  activeFinanceReportCard === "outletSales"
                    ? "border-green-300 bg-green-50 ring-2 ring-green-100"
                    : "bg-white hover:border-slate-300 hover:bg-slate-50"
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold text-gray-500">Outlet Sales</div>
                    <div className="mt-2 text-2xl font-bold text-green-700">
                      {money(financeReportTotals.outletSales)}
                    </div>
                  </div>
                  <div className="rounded-full bg-white px-3 py-1 text-xs font-bold text-green-700">
                    Settlement
                  </div>
                </div>
                <div className="mt-2 text-xs text-gray-500">
                  Selected-period POS collection, payment modes and cashier-wise collection.
                </div>
              </button>
              <button type="button" onClick={() => setActiveFinanceReportCard('dailyFinance')} className={`rounded-lg border p-4 text-left shadow-sm ${activeFinanceReportCard === 'dailyFinance' ? 'border-blue-300 bg-blue-50' : 'bg-white hover:bg-blue-50'}`}><div className="text-sm font-semibold text-gray-500">Daily Finance</div><div className="mt-2 text-2xl font-bold text-blue-700">Installments</div><div className="mt-2 text-xs text-gray-500">Client advances, daily / weekly collections and outstanding balances.</div></button>
              <button type="button" onClick={() => setActiveFinanceReportCard('vendorSales')} className={`rounded-lg border p-4 text-left shadow-sm ${activeFinanceReportCard === 'vendorSales' ? 'border-indigo-300 bg-indigo-50 ring-2 ring-indigo-100' : 'bg-white hover:bg-indigo-50'}`}><div className="text-sm font-semibold text-gray-500">Vendor Sales</div><div className="mt-2 text-sm font-semibold text-indigo-700">Today's Sales Vs Payments</div><div className="mt-3 space-y-2 text-indigo-700">{vendorTodayTotals.loading ? 'Loading…' : vendorTodayTotals.error ? 'Unavailable' : <><div>Sales <strong className="block text-2xl">{money(vendorTodayTotals.sales)}</strong></div><div>Payments <strong className="block text-2xl">{money(vendorTodayTotals.payments)}</strong></div></>}</div><div className="mt-2 text-xs text-gray-500">Date-wise vendor dispatch sales, payment receipts and outstanding balances.</div></button>
                  <button type="button" onClick={() => setActiveFinanceReportCard('pendingPayments')}
                className={`rounded-lg border p-4 text-left shadow-sm transition ${activeFinanceReportCard === 'pendingPayments' ? 'border-amber-300 bg-amber-50 ring-2 ring-amber-100' : 'bg-white hover:bg-amber-50'}`}>
                <div className="text-sm font-semibold text-gray-500">Dispatches & Pending Payments</div>
                <div className="mt-2 text-2xl font-bold text-amber-700">{pendingLoading ? 'Loading…' : pendingError ? 'Unavailable' : money(pendingPayments?.pendingAmount)}</div>
                <div className="mt-2 text-xs text-gray-500">Vendor dispatch revenue, purchase costs, gross profit, payments received and outstanding balances.</div>
              </button>
            </div>

            <div hidden={activeFinanceReportCard !== 'vendorSales'}><VendorSalesReport token={token} data={pendingPayments} loading={pendingLoading} error={pendingError} onRefresh={loadPendingPayments} onTodayTotals={setVendorTodayTotals} /></div>
            {activeFinanceReportCard === 'vendorSales' ? null : activeFinanceReportCard === 'dailyFinance' ? <DailyFinancePanel token={token} refreshKey={dailyFinanceRefresh} /> : activeFinanceReportCard === 'pendingPayments' ? <PendingPaymentsReport token={token} data={pendingPayments} loading={pendingLoading} error={pendingError} onRefresh={loadPendingPayments} /> : activeFinanceReportCard === "reports" ? (
              <div className="mt-4 space-y-4">
                <DispatchFinanceReport token={token} />
                <div className="rounded-lg border bg-white p-4 shadow-sm">
                  <div className="mb-3 font-semibold">Outlet Sales Report Dates</div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <label className="block text-sm font-semibold">
                      From
                      <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="mt-2 block w-full rounded-lg border p-2" />
                    </label>
                    <label className="block text-sm font-semibold">
                      To
                      <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="mt-2 block w-full rounded-lg border p-2" />
                    </label>
                  </div>
                </div>
                <OutletDailyFinanceReport token={token} from={fromDate} to={toDate} />
              </div>
            ) : (
              <div className="mt-4 space-y-3">
                <div className="grid gap-3 rounded-lg border bg-white p-4 shadow-sm md:grid-cols-2 xl:grid-cols-[1fr_1fr_1fr_120px_120px] md:items-end">
                  <label className="block">
                    <span className="text-sm font-bold text-gray-700">Outlet Location</span>
                    <select
                      value={selectedOutlet}
                      onChange={(e) => {
                        setSelectedOutlet(e.target.value);
                        setSelectedCashier("all");
                        setSelectedPaymentMode("all");
                      }}
                      className="mt-2 h-11 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm outline-none focus:border-slate-600 focus:ring-2 focus:ring-slate-200"
                    >
                      <option value="">All Outlets</option>
                      {outletOptions.map(outlet => <option key={outlet} value={outlet}>{outlet}</option>)}
                    </select>
                  </label>
                  <label className="block">
                    <span className="text-sm font-bold text-gray-700">From</span>
                    <input
                      type="date"
                      value={fromDate}
                      onChange={(e) => setFromDate(e.target.value)}
                      className="mt-2 h-11 w-full rounded-lg border border-gray-300 px-3 text-sm outline-none focus:border-slate-600 focus:ring-2 focus:ring-slate-200"
                    />
                  </label>

                  <label className="block">
                    <span className="text-sm font-bold text-gray-700">To</span>
                    <input
                      type="date"
                      value={toDate}
                      onChange={(e) => setToDate(e.target.value)}
                      className="mt-2 h-11 w-full rounded-lg border border-gray-300 px-3 text-sm outline-none focus:border-slate-600 focus:ring-2 focus:ring-slate-200"
                    />
                  </label>

                  <button
                    onClick={loadAccounts}
                    disabled={loading}
                    className="inline-flex h-12 min-w-[120px] items-center justify-center gap-2 rounded-lg bg-slate-700 px-4 text-sm font-bold text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-gray-400"
                  >
                    <RefreshCw size={16} />
                    {loading ? "Loading" : "Refresh"}
                  </button>

                  <button
                    onClick={reloadApp}
                    className="inline-flex h-12 min-w-[120px] items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-4 text-sm font-bold text-gray-700 hover:bg-gray-100"
                  >
                    <RefreshCw size={16} />
                    Reload App
                  </button>
                </div>

                {localError || error ? (
                  <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">
                    {localError || error}
                  </div>
                ) : null}

                <div className="grid gap-3 md:grid-cols-4">
                  <ReportCard label="Orders" value={billedOrders.length} detail="All outlet bills in selected period" />
                  <ReportCard
                    label="Total Collection"
                    value={money(totals.amount)}
                    detail="All payment modes"
                  />
                  <ReportCard
                    label="Cash to Settle"
                    value={money(totals.cash)}
                    detail="Cash payments only"
                    tone="green"
                  />
                  <ReportCard
                    label="QR / Card Total"
                    value={money(totals.digital)}
                    detail="Digital and card payments"
                    tone="indigo"
                  />
                </div>

                <section className="rounded-lg border bg-white p-4 shadow-sm">
                  <h3 className="mb-3 font-bold">Outlet bills — including payment pending</h3>
                  <div className="mb-3 grid gap-3 sm:grid-cols-2"><ReportCard label="Billed Sales" value={money(billedTotal)} detail="Paid and unpaid bills" /><ReportCard label="Pending Payment" value={money(pendingTotal)} detail="Amount still to collect" /></div>
                  <div className="overflow-auto"><table className="w-full text-left text-sm"><thead><tr>{['Bill','Mobile','Cashier','Billed amount','Payment status','Pending'].map(title=><th key={title} className="p-2">{title}</th>)}</tr></thead><tbody>{billedOrders.map(order=><tr key={order._id} className="border-t"><td className="p-2"><button type="button" className="text-indigo-700 underline" onClick={()=>setDetailsOrderId(order._id)}>{order.orderId || order.MK_order_id}</button></td><td className="p-2">{order.phoneNo}</td><td className="p-2">{getOrderCashier(order)}</td><td className="p-2">{money(order.totalPrice)}</td><td className="p-2">{order.isPaid ? 'Paid' : Number(order.paidAmount || 0)>0 ? 'Partially paid' : 'Payment pending'}</td><td className="p-2">{money(order.isPaid ? 0 : Math.max(0,Number(order.totalPrice || 0)-Number(order.paidAmount || 0)))}</td></tr>)}</tbody></table>{!billedOrders.length && <p>No outlet bills in this period.</p>}</div>
                </section>

                <div className="grid gap-3 rounded-lg border bg-white p-4 shadow-sm md:grid-cols-2">
                  <div>
                    <label className="block">
                      <span className="text-sm font-bold text-gray-700">Cashier</span>
                      <select
                        value={selectedCashier}
                        onChange={(e) => setSelectedCashier(e.target.value)}
                        className="mt-2 h-11 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm outline-none focus:border-slate-600 focus:ring-2 focus:ring-slate-200"
                      >
                        <option value="all">All Cashiers</option>
                        {cashierSummary.map((item) => (
                          <option key={item.cashier} value={item.cashier}>
                            {item.cashier}
                          </option>
                        ))}
                      </select>
                    </label>
                    <div className="mt-3 rounded-lg bg-slate-50 px-4 py-3">
                      <div className="text-sm font-semibold text-gray-500">
                        {selectedCashierTotal.label} Billed Amount
                      </div>
                      <div className="mt-1 text-2xl font-bold text-gray-900">
                        {money(selectedCashierTotal.amount)}
                      </div>
                      <div className="mt-1 text-sm text-gray-500">
                        {selectedCashierTotal.count} order
                        {selectedCashierTotal.count === 1 ? "" : "s"}
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block">
                      <span className="text-sm font-bold text-gray-700">
                        Mode of Payment
                      </span>
                      <select
                        value={selectedPaymentMode}
                        onChange={(e) => setSelectedPaymentMode(e.target.value)}
                        className="mt-2 h-11 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm outline-none focus:border-slate-600 focus:ring-2 focus:ring-slate-200"
                      >
                        <option value="all">All Payment Modes</option>
                        {paymentSummary.map((item) => (
                          <option key={item.method} value={item.method}>
                            {item.method}
                          </option>
                        ))}
                      </select>
                    </label>
                    <div className="mt-3 rounded-lg bg-slate-50 px-4 py-3">
                      <div className="text-sm font-semibold text-gray-500">
                        {selectedPaymentTotal.label} Billed Amount
                      </div>
                      <div className="mt-1 text-2xl font-bold text-gray-900">
                        {money(selectedPaymentTotal.amount)}
                      </div>
                      <div className="mt-1 text-sm text-gray-500">
                        {selectedPaymentTotal.count} order
                        {selectedPaymentTotal.count === 1 ? "" : "s"}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="grid gap-3 md:grid-cols-3">
                  {allPaymentSummary.length ? (
                    allPaymentSummary.map((item) => {
                      const Icon = methodIcon[item.method] || WalletCards;
                      return (
                        <div
                          key={item.method}
                          className="rounded-lg border bg-white p-4 shadow-sm"
                        >
                          <div className="flex items-center justify-between gap-3">
                            <div className="flex items-center gap-3">
                              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-slate-100 text-slate-700">
                                <Icon size={20} />
                              </div>
                              <div>
                                <div className="font-bold text-gray-900">
                                  {item.method}
                                </div>
                                <div className="text-sm text-gray-500">
                                  {item.count} order{item.count === 1 ? "" : "s"}
                                </div>
                              </div>
                            </div>
                            <div className="text-right text-lg font-bold text-gray-900">
                              {money(item.amount)}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="rounded-lg border bg-white p-4 text-sm text-gray-500 shadow-sm md:col-span-3">
                      No payment data found for this period.
                    </div>
                  )}
                </div>

                <div className="overflow-hidden rounded-lg border bg-white shadow-sm">
                  <div className="border-b px-4 py-3 text-sm font-bold text-gray-800">
                    Payment Details
                  </div>
                  <div className="max-h-[70vh] overflow-auto">
                    <table className="min-w-full text-sm">
                      <thead className="bg-slate-100">
                        <tr>
                          <th className="px-3 py-2 text-left">Date</th>
                          <th className="px-3 py-2 text-left">Order ID</th>
                          <th className="px-3 py-2 text-left">Outlet</th>
                          <th className="px-3 py-2 text-left">Customer</th>
                          <th className="px-3 py-2 text-left">Payment</th>
                          <th className="px-3 py-2 text-left">Cashier</th>
                          <th className="px-3 py-2 text-right">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loading ? (
                          <tr>
                            <td colSpan="7" className="px-3 py-8 text-center">
                              Loading payments...
                            </td>
                          </tr>
                        ) : selectedOrders.length ? (
                          selectedOrders.map((order) => (
                            <tr key={order._id || order.MK_order_id} className="border-t">
                              <td className="px-3 py-2">
                                {formatDateTime(order.createdAt)}
                              </td>
                              <td className="px-3 py-2">
                                <button type="button"
                                  onClick={() => setDetailsOrderId(order.sourceOrderId || order._id || order.MK_order_id)}
                                  className="font-semibold text-blue-700 underline hover:text-blue-900"
                                  aria-label={`View order ${order.MK_order_id || order.sourceOrderId || order._id}`}>
                                  {order.MK_order_id || order.sourceOrderId || order._id || "-"}
                                </button>
                              </td>
                              <td className="px-3 py-2">{getOrderOutlet(order)}</td>
                              <td className="px-3 py-2">{order.phoneNo || "-"}</td>
                              <td className="px-3 py-2">
                                {order.paymentMethod || "-"}
                              </td>
                              <td className="px-3 py-2">
                                {order.posUserName || posUserInfo?.username || "-"}
                              </td>
                              <td className="px-3 py-2 text-right font-semibold">
                                {money(order.totalPrice)}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td
                              colSpan="7"
                              className="px-3 py-8 text-center text-gray-500"
                            >
                              No payments found.
                            </td>
                          </tr>
                        )}
                      </tbody>
                      <tfoot>
                        <tr className="border-t bg-yellow-100 font-bold">
                          <td colSpan="6" className="px-3 py-3 text-right">
                            Total
                          </td>
                          <td className="px-3 py-3 text-right">
                            {money(selectedTotal.amount)}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>
        {detailsOrderId && <OutletSalesOrderDetails orderId={detailsOrderId} token={token} onClose={() => setDetailsOrderId(null)} />}
      </div>
    </div>
  );

  if (isInventoryView) {
    return <StockManagerLayout>{content}</StockManagerLayout>;
  }

  return content;
}

